import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Film, Trash2, Instagram, Eye, Heart, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const AdminReels = () => {
  const { getAuthHeaders } = useAuth();
  const [reels, setReels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    instagram_url: "",
    video_url: "",
    thumbnail_url: "",
    caption: "",
    product_id: "",
    product_type: "affiliate",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reelsRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/reels`, { headers: getAuthHeaders() }),
        axios.get(`${API}/products/affiliate?limit=100`),
      ]);
      setReels(reelsRes.data.reels || []);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      instagram_url: "",
      video_url: "",
      thumbnail_url: "",
      caption: "",
      product_id: "",
      product_type: "affiliate",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/admin/reels`, {
        instagram_url: form.instagram_url,
        video_url: form.video_url || null,
        thumbnail_url: form.thumbnail_url || null,
        caption: form.caption,
        product_id: form.product_id || null,
        product_type: form.product_type,
      }, { headers: getAuthHeaders() });
      
      toast.success("Reel created successfully!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create reel");
    }
    setSubmitting(false);
  };

  const handleDelete = async (reelId) => {
    if (!confirm("Delete this reel?")) return;

    try {
      await axios.delete(`${API}/admin/reels/${reelId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Reel deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete reel");
    }
  };

  return (
    <AdminLayout title="Manage Reels">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-500">
              Create and manage product reels to showcase affiliate products
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 text-white" data-testid="admin-add-reel-btn">
                <Instagram className="w-4 h-4 mr-2" />
                Add Reel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Instagram className="w-5 h-5" />
                  Add Affiliate Product Reel
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Instagram Reel URL *</Label>
                  <Input
                    value={form.instagram_url}
                    onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                    placeholder="https://www.instagram.com/reel/..."
                    required
                    data-testid="admin-reel-instagram-url"
                  />
                </div>

                <div>
                  <Label>Video (Optional - upload MP4)</Label>
                  <CloudinaryUpload
                    value={form.video_url}
                    onChange={(url) => setForm({ ...form, video_url: url })}
                    folder="reels/"
                    resourceType="video"
                    testId="admin-reel-video-upload"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    For better playback, upload a direct video file (MP4)
                  </p>
                </div>

                <div>
                  <Label>Thumbnail (Optional)</Label>
                  <CloudinaryUpload
                    value={form.thumbnail_url}
                    onChange={(url) => setForm({ ...form, thumbnail_url: url })}
                    folder="reels/"
                    resourceType="image"
                    testId="admin-reel-thumbnail-upload"
                  />
                </div>

                <div>
                  <Label>Caption *</Label>
                  <Textarea
                    value={form.caption}
                    onChange={(e) => setForm({ ...form, caption: e.target.value })}
                    placeholder="Describe the product..."
                    required
                  />
                </div>

                <div>
                  <Label>Link to Affiliate Product (Optional)</Label>
                  <Select
                    value={form.product_id || "none"}
                    onValueChange={(v) => setForm({ ...form, product_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No product linked</SelectItem>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.brand_name} - {product.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500"
                  disabled={submitting}
                  data-testid="admin-submit-reel-btn"
                >
                  {submitting ? "Creating..." : "Create Reel"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-100">
                <Film className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{reels.length}</p>
                <p className="text-sm text-gray-500">Total Reels</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {reels.reduce((sum, r) => sum + (r.views_count || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Total Views</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100">
                <Heart className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {reels.reduce((sum, r) => sum + (r.likes_count || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Total Likes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="aspect-[9/16] bg-gray-200 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : reels.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {reels.map((reel) => (
            <Card key={reel.id} className="border-0 shadow-sm overflow-hidden group">
              <div className="aspect-[9/16] bg-gray-900 relative">
                {reel.thumbnail_url ? (
                  <img
                    src={reel.thumbnail_url}
                    alt={reel.caption}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500">
                    <Film className="w-12 h-12 text-white/50" />
                  </div>
                )}
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <a
                    href={reel.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white rounded-full"
                  >
                    <ExternalLink className="w-5 h-5 text-gray-900" />
                  </a>
                  <button
                    onClick={() => handleDelete(reel.id)}
                    className="p-3 bg-red-500 rounded-full"
                  >
                    <Trash2 className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Stats */}
                <div className="absolute bottom-2 left-2 right-2 flex items-center gap-3 text-white text-sm">
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    {reel.likes_count || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {reel.views_count || 0}
                  </span>
                </div>

                {/* Source Badge */}
                <div className="absolute top-2 right-2">
                  <Badge className={`text-xs ${reel.seller_id ? 'bg-blue-500' : 'bg-purple-500'}`}>
                    {reel.seller_id ? 'Seller' : 'Admin'}
                  </Badge>
                </div>

                {/* Instagram Badge */}
                <div className="absolute top-2 left-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500">
                    <Instagram className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="text-sm text-gray-700 line-clamp-2">{reel.caption}</p>
                {reel.product_id && (
                  <Badge className="mt-2 bg-primary-100 text-primary-700 text-xs">
                    Product Linked
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 flex items-center justify-center">
              <Instagram className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reels Yet</h3>
            <p className="text-gray-500 mb-4 max-w-md mx-auto">
              Create engaging product reels to showcase affiliate products and drive sales.
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Reel
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminReels;
